import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  LOCALE_META,
  type Locale,
  type LocaleMeta,
} from '@caliu/types'

const SUPPORTED_SET = new Set<string>(SUPPORTED_LOCALES as readonly string[])

function normalize(raw: string): string {
  return raw.trim().replace('_', '-')
}

function langOnly(tag: string): string {
  const dash = tag.indexOf('-')
  return dash === -1 ? tag : tag.slice(0, dash)
}

/**
 * Map a single BCP-47 candidate to a supported Locale, or null if unsupported.
 * Handles exact match (`es` → `es`), region-aware match (`zh-CN` → `zh-CN`),
 * region fallback (`es-AR` → `es`, `ko-KR` → `ko`), Apple/Android script tags
 * (`zh-Hans` → `zh-CN`, `zh-Hant` → `zh-TW`), Traditional-reading regions
 * (`zh-HK` / `zh-MO` → `zh-TW`), and case-insensitive tags (`zh-cn` → `zh-CN`).
 */
function matchOne(raw: string): Locale | null {
  const tag = normalize(raw)
  if (!tag) return null

  if (SUPPORTED_SET.has(tag)) return tag as Locale

  const lower = tag.toLowerCase()
  for (const supported of SUPPORTED_LOCALES) {
    if (supported.toLowerCase() === lower) return supported
  }

  // Special: Apple/Android emit zh-Hans / zh-Hant. Map Hans → zh-CN.
  if (lower === 'zh-hans' || lower.startsWith('zh-hans-')) return 'zh-CN'
  // ...and Hant → zh-TW, covering zh-Hant-TW / zh-Hant-HK too.
  if (lower === 'zh-hant' || lower.startsWith('zh-hant-')) return 'zh-TW'
  // Hong Kong and Macau read Traditional even without a script subtag.
  // (`zh-TW` itself already matched above.)
  if (lower === 'zh-hk' || lower === 'zh-mo') return 'zh-TW'
  // Bare `zh` is deliberately left unmapped: it carries no script or region
  // signal, so it falls through to English rather than guessing Simplified.

  const primary = langOnly(lower)
  if (SUPPORTED_SET.has(primary)) return primary as Locale

  return null
}

/**
 * Pick the best supported Locale from a list of BCP-47 candidates.
 * Earlier candidates win on ties. Returns DEFAULT_LOCALE if nothing matches.
 *
 * Pure function — used by both the web app (navigator.languages) and the
 * marketing site (Accept-Language) so detection is consistent across surfaces.
 */
export function resolveLocale(candidates: readonly string[] | null | undefined): Locale {
  if (!candidates || candidates.length === 0) return DEFAULT_LOCALE
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue
    const hit = matchOne(candidate)
    if (hit) return hit
  }
  return DEFAULT_LOCALE
}

export { DEFAULT_LOCALE, SUPPORTED_LOCALES, LOCALE_META }
export type { Locale, LocaleMeta }
